import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { readonly, ref, type Ref } from 'vue'
import { littaApiFetchJson, waitForAuthUser } from '@/lib/api/litta-api-fetch'

/** Role keys held per `litta.applications.key`. */
export type RolesByApplication = Record<string, string[]>

export interface Me {
    email: string | null
    name: string | null
    roles: RolesByApplication
    userId: string | null
}

/**
 * Signs the caller in to the platform and returns what they may do, everywhere.
 *
 * A POST because it writes: a first sign-in provisions the `users` row and
 * records that this account has signed in to Beehive.
 */
export async function fetchMe(): Promise<Me> {
    return littaApiFetchJson<Me>('/auth/me', {
        method: 'POST',
        body: JSON.stringify({ application: 'beehive' }),
    })
}

/** Who Firebase currently says is signed in; null between sign-out and sign-in. */
export function currentUid(): string | null {
    return getAuth().currentUser?.uid ?? null
}

const uidRef = ref<string | null>(null)
let observing = false

export function useCurrentUid(): Readonly<Ref<string | null>> {
    if (!observing) {
        observing = true
        uidRef.value = currentUid()
        onAuthStateChanged(getAuth(), user => {
            uidRef.value = user?.uid ?? null
        })
    }
    return readonly(uidRef)
}

const ME_TTL_MS = 60_000

let cached: {
    expiresAt: number
    promise: Promise<Me>
    uid: string | null
} | null = null

export function resetMe(): void {
    cached = null
}

/**
 * Shared with the router guard, which runs before any component mounts and so
 * cannot use the vue-query hook. One in-flight request regardless of how many
 * navigations happen while it resolves. Keyed by uid, so signing out and in
 * as somebody else does not inherit the first person's roles, and expiring,
 * so a refetch is a real one rather than the same promise handed back.
 */
export async function fetchMeCached(): Promise<Me> {
    /* Awaited rather than read, so a deep link during Firebase's startup is
     * cached under the person it resolves to, not under "nobody yet". */
    const uid = (await waitForAuthUser())?.uid ?? null
    const now = Date.now()
    if (cached?.uid !== uid || cached.expiresAt <= now) {
        const promise = fetchMe().catch((error: unknown) => {
            cached = null
            console.warn(
                'Could not read roles from POST /auth/me, so every role-gated area will be hidden.',
                error,
            )
            throw error
        })
        cached = { expiresAt: now + ME_TTL_MS, promise, uid }
    }
    return cached.promise
}
